import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  activeTab?: string;
  setActiveTab: (tab: string) => void;
  onTabChange?: (tab: string) => void;
  setOnTabChange: (callback: ((tab: string) => void) | undefined) => void;
  leadName?: string;
  setLeadName: (name: string | undefined) => void;
  onOpenCommunication?: (type: 'email' | 'call') => void;
  setOnOpenCommunication: (callback: ((type: 'email' | 'call') => void) | undefined) => void;
  onOpenChat?: () => void;
  setOnOpenChat: (callback: (() => void) | undefined) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>();
  const [onTabChangeCallback, setOnTabChangeCallback] = useState<((tab: string) => void) | undefined>();
  const [leadName, setLeadName] = useState<string>();
  const [onOpenCommunicationCallback, setOnOpenCommunicationCallback] = useState<((type: 'email' | 'call') => void) | undefined>();
  const [onOpenChatCallback, setOnOpenChatCallback] = useState<(() => void) | undefined>();

  const openSidebar = useCallback(() => setIsOpen(true), []);
  const closeSidebar = useCallback(() => setIsOpen(false), []);
  const toggleSidebar = useCallback(() => setIsOpen(prev => !prev), []);

  const handleSetOnTabChange = useCallback((callback: ((tab: string) => void) | undefined) => {
    setOnTabChangeCallback(() => callback);
  }, []);

  const handleSetOnOpenCommunication = useCallback((callback: ((type: 'email' | 'call') => void) | undefined) => {
    setOnOpenCommunicationCallback(() => callback);
  }, []);

  const handleSetOnOpenChat = useCallback((callback: (() => void) | undefined) => {
    setOnOpenChatCallback(() => callback);
  }, []);

  return (
    <SidebarContext.Provider 
      value={{ 
        isOpen, setIsOpen, openSidebar, closeSidebar, toggleSidebar,
        activeTab, setActiveTab,
        onTabChange: onTabChangeCallback,
        setOnTabChange: handleSetOnTabChange,
        leadName, setLeadName,
        onOpenCommunication: onOpenCommunicationCallback,
        setOnOpenCommunication: handleSetOnOpenCommunication,
        onOpenChat: onOpenChatCallback,
        setOnOpenChat: handleSetOnOpenChat
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
